import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRootStore } from 'store/rootStore';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { RateStatistic, StatisticCard, StatisticsFields, StatisticsValues, RateCount } from 'types/electionService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Clipboard, ShieldHeart } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import {
  ProductLineSectionType,
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import Button from '@insurancemenu/imenu-components-lib/dist/components/Button';
import { convertRateValue } from 'global/utils/convertRateValue';
import { domainServiceHelper } from 'global/services-helpers';
import { useQueryParams } from '@insurancemenu/imenu-components-lib';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import { createStyles } from './styles';

export const calculateStatistics = (input: Omit<StatisticsValues, 'estimatedCost'>): StatisticsFields => {
  return {
    total: {
      value: input.total,
      title: 'Total',
    },
    elected: {
      value: input.elected,
      title: 'Elected',
    },
    electionsMissing: {
      value: input.electionsMissing,
      title: 'Elections Missing',
    },
    waived: {
      value: input.waived,
      title: 'Waived',
    },
    invited: {
      value: input.invited,
      title: 'Invited',
    },
    registered: {
      value: input.registered,
      title: 'Registered',
    },
  };
};

const INITIAL_STATISTICS: StatisticsFields = calculateStatistics({
  total: 0,
  elected: 0,
  electionsMissing: 0,
  waived: 0,
  invited: 0,
  registered: 0,
});

const ElectionStatistics = observer((): JSX.Element => {
  const [statistics, setStatistics] = useState<StatisticsFields>(INITIAL_STATISTICS);

  const {
    employeeElectionsStore: { loading, statistic, rateStats, getStatistic },
  } = useRootStore();
  const [showStatisticModal, setShowStatisticModal] = useState(false);
  const { periodId } = useParams<{ periodId: string }>();
  const query = useQueryParams();
  const groupId = query.get('groupId') || '';

  const convertedStatistic = toJS(statistic);
  const convertedRateStatistic = toJS(rateStats);

  useEffect(() => {
    void getStatistic(groupId, periodId);
  }, []);

  useEffect(() => {
    if (convertedStatistic) setStatistics(calculateStatistics(convertedStatistic));
  }, [statistic]);

  const generalPLineFromStatistic: ProductLineId[] =
    (convertedRateStatistic &&
      domainServiceHelper
        .filterPLineByType(Object.keys(convertedRateStatistic) as ProductLineId[], ProductLineSectionType.GENERAL)
        .sort(sortByProductLineId)) ||
    [];

  const ancillaryPLineFromStatistic =
    (convertedRateStatistic &&
      domainServiceHelper
        .filterPLineByType(Object.keys(convertedRateStatistic) as ProductLineId[], ProductLineSectionType.ANCILLARY)
        .sort(sortByProductLineId)) ||
    [];

  const ancillaryPLinesQuantity = ancillaryPLineFromStatistic.length;
  const allCardsQuantity = generalPLineFromStatistic.length + 1 + (ancillaryPLinesQuantity > 0 ? 1 : 0);
  const classes = createStyles({ allCardsQuantity, ancillaryPLinesQuantity });

  const getValueFromStatistics = (statisticsObj: StatisticsFields, key: keyof StatisticsFields): StatisticCard => {
    return statisticsObj[key];
  };
  const getValueFromRateStatistics = (statisticsObj: RateStatistic, key: keyof RateStatistic): RateCount => {
    return statisticsObj[key];
  };

  const generalStatisticItems = Object.keys(statistics).map((key: string): JSX.Element => {
    const statisticsCard = getValueFromStatistics(statistics, key as keyof StatisticsFields);
    return (
      <li key={key} className={classes.cardInfo}>
        <p className={classes.filterCardTitle}>{statisticsCard.title}</p>
        <p className={classes.filterCardQuantity}>{statisticsCard.value}</p>
      </li>
    );
  });

  const loadingOverlay = loading && classes.loadingOverlay;

  const createStatisticCard = (productLineId: ProductLineId): JSX.Element | null => {
    if (!convertedRateStatistic) return null;
    const rateStatisticCount = getValueFromRateStatistics(convertedRateStatistic, productLineId);
    const isProductLineGeneral = PRODUCT_LINES_CONTENT_MAP[productLineId]?.section === ProductLineSectionType.GENERAL;
    const rateInfo = Object.keys(rateStatisticCount).map((rateName) => {
      return (
        <li key={`${productLineId}-${rateName}`} className={classes.cardInfo}>
          <p className={classes.filterCardTitle}>{rateName}</p>
          <p className={classes.filterCardQuantity}>{rateStatisticCount[rateName]}</p>
        </li>
      );
    });

    return (
      <li
        className={clsx(classes.filterCard, !isProductLineGeneral && classes.additionalFilterCard, loadingOverlay)}
        key={`${productLineId}`}
      >
        <ProductLineTitle
          productLineId={productLineId}
          styles={clsx(classes.cardName, !isProductLineGeneral && classes.additionalCardName)}
        />
        <ul className={classes.rateStatisticList}>{rateInfo}</ul>
        {rateInfo.length > 0 && (
          <div className={classes.totalPremium}>
            <p className={classes.filterCardTitle}>Monthly Cost:</p>
            <p className={classes.monthlyCost}>
              {convertRateValue(
                convertedStatistic?.estimatedCost[productLineId]
                  ? convertedStatistic?.estimatedCost[productLineId].monthlyCost
                  : '0.00',
              )}
            </p>
          </div>
        )}
      </li>
    );
  };

  const ancillaryPLinesQuantityLabel = `${ancillaryPLinesQuantity} product ${
    ancillaryPLinesQuantity === 1 ? 'line' : 'lines'
  }`;

  const statisticCardsForGeneralPLines =
    generalPLineFromStatistic.map((productLineId: ProductLineId) => createStatisticCard(productLineId)) || [];

  const statisticCardsForAncillaryPLines =
    ancillaryPLineFromStatistic.map((productLineId: ProductLineId) => createStatisticCard(productLineId)) || [];

  return (
    <>
      <ul className={classes.statisticsBlock}>
        <li className={clsx(classes.filterCard, loadingOverlay)}>
          <p className={clsx(classes.cardNameWrapper, classes.cardName)}>
            <Clipboard width="2.4rem" height="2.4rem" />
            <span className={classes.generalStatisticTitle}>General</span>
          </p>
          <ul className={classes.generalStatisticList}>{generalStatisticItems}</ul>
        </li>
        {statisticCardsForGeneralPLines}
        {ancillaryPLinesQuantity > 0 && (
          <li className={clsx(classes.filterCard, loadingOverlay)}>
            <p className={clsx(classes.cardNameWrapper, classes.cardName)}>
              <ShieldHeart width="2.4rem" height="2.4rem" />
              <span className={classes.generalStatisticTitle}>Ancillary</span>
            </p>
            <span className={classes.ancillaryStatisticLabel}>{ancillaryPLinesQuantityLabel}</span>
            <Button
              type="opaque"
              color="primary"
              className={classes.showStatisticButton}
              onClick={(): void => {
                setShowStatisticModal(true);
              }}
            >
              Show statistics
            </Button>
          </li>
        )}
      </ul>
      {ancillaryPLinesQuantity > 0 && (
        <ActionModal
          show={showStatisticModal}
          modalType={ModalType.NO_ICON}
          className={classes.statisticModal}
          title="Ancillary product line statistics"
          subtitle={ancillaryPLinesQuantityLabel}
          modalContent={<ul className={classes.additionalCardsList}>{statisticCardsForAncillaryPLines}</ul>}
          onClose={(): void => {
            setShowStatisticModal(false);
          }}
        />
      )}
    </>
  );
});

export default ElectionStatistics;
